import Lenis from '@studio-freight/lenis';
import { gsap, ScrollTrigger } from '../gsap-config.js';
import { initAnimationsForElement, cleanupAnimationsForElement } from '../utils/animations.js';
import { clearGsapInlineProps, resetGsapElement } from '../utils/gsap.js';
import {
	prefersReducedMotion,
	matchesDesktopLayout,
	matchesCompactLayout,
	matchesSmallNavLayout,
	DESKTOP_LAYOUT,
} from '../utils/media.js';
import { startLenisRaf, createLenisScrollBus } from '../utils/scroll.js';

let dotlottieBundlePromise = null;

function canPlayRailLottie() {
	return matchesDesktopLayout() && !prefersReducedMotion();
}

function loadDotlottieBundle() {
	if (!dotlottieBundlePromise) {
		dotlottieBundlePromise = import('../vendor/dotlottie-bundle.js');
	}

	return dotlottieBundlePromise;
}

class ServicesMain extends HTMLElement {
	constructor() {
		super();
		this.lenis = null;
		this.resizeTimer = null;
	}

	connectedCallback() {
		this._abort = new AbortController();
		this._listenerOptions = { signal: this._abort.signal };

		this.initializeLenis();
		this.initializeNavVisibility();
		this.initializeScrollToAnchor();
		this.initializeResizeHandler();
		this.initializeDepthStack();
		this.initializeRailPin();
		this.initializeActiveState();
		this.scheduleLottiePlayer();
		window.matchMedia(DESKTOP_LAYOUT).addEventListener(
			'change',
			(event) => {
				if (event.matches) this.scheduleLottiePlayer();
			},
			this._listenerOptions,
		);

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				initAnimationsForElement(this);
			});
		});
	}

	disconnectedCallback() {
		this._abort?.abort();
		this._abort = null;

		this._scrollBus?.unsubscribe();
		this._scrollBus = null;

		this._stopLenisRaf?.();
		this._stopLenisRaf = null;

		if (this.lenis) {
			this.lenis.destroy();
			this.lenis = null;
		}

		this._depthGsapContext?.revert();
		this._depthGsapContext = null;

		this.activeStateObserver?.disconnect();
		this.activeStateObserver = null;

		this._lottieObserver?.disconnect();
		this._lottieObserver = null;

		if (this._lottieIdleId != null && typeof cancelIdleCallback === 'function') {
			cancelIdleCallback(this._lottieIdleId);
		}
		this._lottieIdleId = null;
		clearTimeout(this._lottieTimer);
		this._lottieTimer = null;
		this._lottieStarted = false;
		this._pendingLottieSrc = null;

		this.focusCategory = null;
		this._updateDepth = null;
		this._depthNavLock = false;

		cleanupAnimationsForElement(this);

		if (this.nav?.parentElement === document.body) {
			this.nav.remove();
		}
	}

	initializeLenis() {
		this.lenis = new Lenis({
			duration: 1.2,
			easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
			orientation: 'vertical',
			gestureOrientation: 'vertical',
			smoothWheel: true,
			wheelMultiplier: 1,
			smoothTouch: false,
			touchMultiplier: 2,
			infinite: false,
		});

		this._stopLenisRaf = startLenisRaf(this.lenis);
		this._scrollBus = createLenisScrollBus(this.lenis);
	}

	initializeScrollToAnchor() {
		if (!this.nav) return;

		this.nav.querySelectorAll('.nav-link[data-anchor-target]').forEach((element) => {
			element.addEventListener(
				'click',
				(e) => {
					e.preventDefault();
					const target = element.getAttribute('data-anchor-target');
					const section = this.querySelector(target);
					const firstRow = section?.querySelector('[data-depth-row]');
					const focusLine = window.innerHeight * 0.42;
					const scrollBefore = this.lenis?.scroll ?? window.scrollY;
					const scrollEasing = (x) => (x < 0.5 ? 8 * x * x * x * x : 1 - Math.pow(-2 * x + 2, 4) / 2);

					let scrollTarget = target;
					let scrollOffset = matchesSmallNavLayout() ? -10 : -100;

					if (!matchesSmallNavLayout() && firstRow) {
						const rowRect = firstRow.getBoundingClientRect();
						scrollTarget = scrollBefore + rowRect.top + firstRow.offsetHeight / 2 - focusLine;
						scrollOffset = 0;
					}

					this.setActiveNavLink(element);
					this.currentActiveSection = target.replace('#', '');

					this._depthNavLock = true;
					this.focusCategory?.(target);

					this.lenis.scrollTo(scrollTarget, {
						offset: scrollOffset,
						duration: 1.2,
						easing: scrollEasing,
						onComplete: () => {
							this._depthNavLock = false;
							this._updateDepth?.();
						},
					});
				},
				this._listenerOptions
			);
		});
	}

	initializeResizeHandler() {
		window.addEventListener(
			'resize',
			() => {
				if (!this.lenis) return;
				this.lenis.stop();
				clearTimeout(this.resizeTimer);
				this.resizeTimer = setTimeout(() => {
					this.lenis.start();
				}, 250);
			},
			this._listenerOptions
		);
	}

	initializeNavVisibility() {
		const nav = this.querySelector('.nav');
		const footer = document.getElementById('tt-footer');

		if (!nav) return;

		if (nav.parentElement === this) {
			document.body.appendChild(nav);
		}

		this.nav = nav;
		this.navProgressFill = nav.querySelector('.nav__progress-fill');

		this._scrollBus.subscribe(({ scroll, limit }) => {
			const liveRect = this.getBoundingClientRect();
			this.servicesTop = scroll + liveRect.top;
			this.servicesHeight = liveRect.height;

			this.updateNavProgress(scroll, limit);

			const viewportTop = scroll;
			const viewportBottom = scroll + window.innerHeight;
			const servicesBottom = this.servicesTop + this.servicesHeight;
			const hasScrolledPast = viewportTop > servicesBottom;
			const hasntReachedYet = viewportBottom < this.servicesTop;
			const isInServicesSection = !hasScrolledPast && !hasntReachedYet;

			if (!isInServicesSection) {
				nav.classList.remove('nav--visible');
				nav.classList.add('nav--hidden');
				return;
			}

			nav.classList.remove('nav--hidden');

			if (!footer) {
				if (scroll >= window.innerHeight * 0.6) {
					nav.classList.add('nav--visible');
				} else {
					nav.classList.remove('nav--visible');
				}
				return;
			}

			const footerRect = footer.getBoundingClientRect();
			const footerTop = footerRect.top;
			const viewportHeight = window.innerHeight;
			const footerNearBottom = footerTop < viewportHeight - 100;
			const distanceFromBottom = limit - scroll;
			const footerHeight = footerRect.height || 200;
			const nearFooter = distanceFromBottom < footerHeight + 150;

			if (scroll >= window.innerHeight * 0.6 && !footerNearBottom && !nearFooter) {
				nav.classList.add('nav--visible');
				nav.classList.remove('nav--hidden');
			} else {
				nav.classList.remove('nav--visible');
				nav.classList.add('nav--hidden');
			}
		});
	}

	updateNavProgress(scroll, limit) {
		if (!this.navProgressFill || !this.servicesHeight) return;

		const range = Math.max(1, this.servicesHeight - window.innerHeight);
		const relativeScroll = scroll - this.servicesTop;
		const progress = Math.min(1, Math.max(0, relativeScroll / range));

		this.navProgressFill.style.transform = `scaleX(${progress})`;
	}

	initializeRailPin() {
		const depth = this.querySelector('.services-depth');
		const railColumn = this.querySelector('.services-depth__rail');
		const railSticky = this.querySelector('.services-depth__rail-sticky');
		if (!depth || !railColumn || !railSticky) return;

		const desktopQuery = window.matchMedia(DESKTOP_LAYOUT);

		const readStickyTopOffset = () => {
			const parsed = parseFloat(getComputedStyle(railSticky).top);
			return Number.isFinite(parsed) ? parsed : 132;
		};

		let topOffset = readStickyTopOffset();

		const refreshTopOffset = () => {
			if (
				!railSticky.classList.contains('is-pinned') &&
				!railSticky.classList.contains('is-pinned-bottom')
			) {
				topOffset = readStickyTopOffset();
			}
		};

		const resetRailPin = () => {
			railSticky.classList.remove('is-pinned', 'is-pinned-bottom');
			railSticky.style.removeProperty('left');
			railSticky.style.removeProperty('width');
		};

		const updateRailPin = () => {
			if (!desktopQuery.matches) {
				resetRailPin();
				return;
			}

			const depthRect = depth.getBoundingClientRect();
			const columnRect = railColumn.getBoundingClientRect();
			const railHeight = railSticky.offsetHeight;
			const viewportHeight = window.innerHeight;

			if (depthRect.bottom <= 0 || depthRect.top >= viewportHeight) {
				resetRailPin();
				return;
			}

			const pinStart = depthRect.top <= topOffset;
			const pinEnd = depthRect.bottom <= topOffset + railHeight;

			if (!pinStart) {
				resetRailPin();
				return;
			}

			if (pinEnd) {
				railSticky.classList.remove('is-pinned');
				railSticky.classList.add('is-pinned-bottom');
				railSticky.style.removeProperty('left');
				railSticky.style.removeProperty('width');
				return;
			}

			railSticky.classList.remove('is-pinned-bottom');
			railSticky.classList.add('is-pinned');
			railSticky.style.left = `${columnRect.left}px`;
			railSticky.style.width = `${columnRect.width}px`;
		};

		this._scrollBus.subscribe(updateRailPin);

		window.addEventListener(
			'resize',
			() => {
				refreshTopOffset();
				updateRailPin();
			},
			this._listenerOptions
		);

		requestAnimationFrame(updateRailPin);
	}

	initializeDepthStack() {
		const reducedMotion = prefersReducedMotion();
		const rows = Array.from(this.querySelectorAll('[data-depth-row]'));
		const chapters = Array.from(this.querySelectorAll('[data-service-chapter]'));
		const rail = this.querySelector('.services-depth__rail');

		if (!rows.length) return;

		this.depthRows = rows;
		this.depthChapters = chapters;
		this.depthRail = rail;
		this.depthReducedMotion = reducedMotion;
		this.activeDepthRow = null;
		this.activeDepthChapter = chapters[0] || null;

		const railCounter = rail?.querySelector('[data-rail-counter]');
		const railEyebrow = rail?.querySelector('[data-rail-eyebrow]');
		const railTitle = rail?.querySelector('[data-rail-title]');
		const railDesc = rail?.querySelector('[data-rail-desc]');
		const railSeo = rail?.querySelector('[data-rail-seo]');
		const railLottie = rail?.querySelector('[data-rail-lottie]');
		const categoryCount = chapters.length;

		const setActiveRow = (row) => {
			rows.forEach((item) => item.classList.remove('is-active'));
			this.activeDepthRow = row;
			if (row) row.classList.add('is-active');
		};

		const loadRailLottie = (src) => {
			this.loadRailLottie(railLottie, src);
		};

		const updateRail = (chapter, { force = false } = {}) => {
			if (!rail || !chapter) return;

			const chapterSlug = chapter.dataset.chapterSlug || '';
			const chapterChanged = this._lastRailSlug !== chapterSlug;

			if (!force && !chapterChanged && this.activeDepthChapter === chapter) return;
			if (force && !chapterChanged) return;

			this.activeDepthChapter = chapter;
			this._lastRailSlug = chapterSlug;
			const index = Number(chapter.dataset.chapterIndex) || 1;
			const paddedIndex = index < 10 ? `0${index}` : `${index}`;
			const paddedTotal = categoryCount < 10 ? `0${categoryCount}` : `${categoryCount}`;
			const lottieSrc = chapter.dataset.lottieSrc || '';

			rail.classList.add('is-updating');

			window.setTimeout(() => {
				if (railCounter) railCounter.textContent = `${paddedIndex} / ${paddedTotal}`;
				if (railEyebrow) railEyebrow.textContent = chapter.dataset.chapterHeadline || '';
				if (railTitle) railTitle.textContent = chapter.dataset.chapterTitle || '';
				if (railDesc) railDesc.textContent = chapter.dataset.chapterDesc || '';

				if (railSeo) {
					const seo = chapter.dataset.chapterSeo || '';
					if (seo) {
						railSeo.textContent = seo;
						railSeo.hidden = false;
					} else {
						railSeo.textContent = '';
						railSeo.hidden = true;
					}
				}

				if (lottieSrc) loadRailLottie(lottieSrc);

				rail.classList.remove('is-updating');
			}, 120);
		};

		const applyRowDepthStyles = () => {
			rows.forEach((row) => {
				clearGsapInlineProps(row);
				row.classList.remove('is-near');
			});
		};

		this.focusCategory = (sectionSelector) => {
			const section = this.querySelector(sectionSelector);
			if (!section) return;

			const firstRow = section.querySelector('[data-depth-row]');
			setActiveRow(firstRow);

			const chapterChanged = this._lastRailSlug !== section.dataset.chapterSlug;
			if (chapterChanged) updateRail(section, { force: true });

			if (matchesDesktopLayout() && !reducedMotion) {
				applyRowDepthStyles();
			}

			section.querySelectorAll('[data-depth-row]').forEach((row) => {
				this._revealDepthRowInstant(row);
			});
		};

		const resetDepthState = () => {
			rows.forEach((row) => {
				clearGsapInlineProps(row, 'transform,opacity,filter');
				row.classList.remove('is-active', 'is-near');
			});
			this.activeDepthRow = null;
			this.activeDepthChapter = null;
		};

		const updateDepth = () => {
			const depthEl = this.querySelector('.services-depth');
			const depthRect = depthEl?.getBoundingClientRect();
			const sectionOffscreen = depthRect && (depthRect.bottom < 0 || depthRect.top > window.innerHeight);

			if (sectionOffscreen) {
				resetDepthState();
				return;
			}

			if (this._depthNavLock) return;

			if (matchesCompactLayout() || reducedMotion) {
				rows.forEach((row) => {
					clearGsapInlineProps(row, 'transform,opacity,filter');
					row.classList.remove('is-near');
				});

				let activeRow = null;
				let bestDistance = Infinity;
				const focusLine = window.innerHeight * 0.42;

				rows.forEach((row) => {
					const rect = row.getBoundingClientRect();
					const center = rect.top + rect.height / 2;
					const distance = Math.abs(center - focusLine);
					if (distance < bestDistance) {
						bestDistance = distance;
						activeRow = row;
					}
				});

				setActiveRow(activeRow);
				const chapter = activeRow?.closest('[data-service-chapter]');
				if (chapter) updateRail(chapter);
				return;
			}

			const focusLine = window.innerHeight * 0.42;
			const maxDistance = window.innerHeight * 0.55;
			const rowProximity = (row) => {
				const rect = row.getBoundingClientRect();
				const center = rect.top + rect.height / 2;
				const distance = Math.abs(center - focusLine);
				return Math.max(0, 1 - distance / maxDistance);
			};

			let activeRow = this.activeDepthRow;
			let bestProximity = activeRow ? rowProximity(activeRow) : -1;

			rows.forEach((row) => {
				const proximity = rowProximity(row);
				const switchThreshold = row === this.activeDepthRow ? 0 : 0.1;
				if (proximity > bestProximity + switchThreshold) {
					bestProximity = proximity;
					activeRow = row;
				}
			});

			setActiveRow(activeRow);
			applyRowDepthStyles();

			const chapter = activeRow?.closest('[data-service-chapter]');
			if (chapter) updateRail(chapter);
		};

		this._updateDepth = updateDepth;
		this._revealedDepthRows = new WeakSet();

		this._revealDepthRowInstant = (row) => {
			if (!row || this._revealedDepthRows.has(row)) return;
			resetGsapElement(row);
			this._revealedDepthRows.add(row);
		};

		this._revealDepthRowAnimated = (row) => {
			if (!row || this._revealedDepthRows.has(row)) return;

			this._revealedDepthRows.add(row);
			gsap.fromTo(
				row,
				{ y: 40, autoAlpha: 0 },
				{
					y: 0,
					autoAlpha: 1,
					duration: 0.7,
					ease: 'power2.out',
					clearProps: 'transform,opacity,filter',
				}
			);
		};

		rows.forEach((row) => {
			const focusRow = () => {
				setActiveRow(row);
				if (matchesDesktopLayout() && !reducedMotion) {
					applyRowDepthStyles();
				}
			};

			row.addEventListener('click', focusRow, this._listenerOptions);
			row.addEventListener('focusin', focusRow, this._listenerOptions);
		});

		this._scrollBus.subscribe(updateDepth);

		window.addEventListener('resize', updateDepth, this._listenerOptions);

		requestAnimationFrame(updateDepth);

		if (!reducedMotion) {
			this._depthGsapContext = gsap.context(() => {
				rows.forEach((row) => {
					ScrollTrigger.create({
						trigger: row,
						start: 'top 92%',
						once: true,
						onEnter: () => {
							if (this._depthNavLock || matchesCompactLayout()) {
								this._revealDepthRowInstant(row);
								return;
							}
							this._revealDepthRowAnimated(row);
						},
					});
				});
			}, this);
		}
	}

	initializeActiveState() {
		const sections = this.querySelectorAll('[data-service-chapter]');
		const navLinks = this.nav?.querySelectorAll('.nav-link[data-anchor-target]');

		if (!sections.length || !navLinks?.length) return;

		this.sectionNavMap = new Map();
		navLinks.forEach((link) => {
			const target = link.getAttribute('data-anchor-target');
			if (target) {
				this.sectionNavMap.set(target.replace('#', ''), link);
			}
		});

		this.currentActiveSection = null;

		this.activeStateObserver = new IntersectionObserver(
			(entries) => {
				if (this._depthNavLock) return;

				let mostVisibleEntry = null;
				let maxIntersectionRatio = 0;

				entries.forEach((entry) => {
					if (entry.isIntersecting && entry.intersectionRatio > maxIntersectionRatio) {
						maxIntersectionRatio = entry.intersectionRatio;
						mostVisibleEntry = entry;
					}
				});

				if (mostVisibleEntry && mostVisibleEntry.target.id !== this.currentActiveSection) {
					const navLink = this.sectionNavMap.get(mostVisibleEntry.target.id);
					if (navLink) {
						this.currentActiveSection = mostVisibleEntry.target.id;
						this.setActiveNavLink(navLink);
					}
				}
			},
			{
				root: null,
				rootMargin: '-30% 0px -30% 0px',
				threshold: [0, 0.25, 0.5, 0.75, 1],
			}
		);

		sections.forEach((section) => {
			if (section.id) this.activeStateObserver.observe(section);
		});

		this._scrollBus.subscribe(() => {
			this.updateActiveStateOnScroll();
		});

		requestAnimationFrame(() => {
			this.updateActiveStateOnScroll();
			if (this.lenis) {
				this.updateNavProgress(this.lenis.scroll, this.lenis.limit);
			}
		});
	}

	setActiveNavLink(activeLink) {
		this.nav?.querySelectorAll('.nav-link').forEach((link) => {
			link.classList.remove('nav-link--active');
		});

		if (activeLink) {
			activeLink.classList.add('nav-link--active');
		}
	}

	updateActiveStateOnScroll() {
		if (this._depthNavLock) return;

		const sections = Array.from(this.querySelectorAll('[data-service-chapter]'));
		const viewportHeight = window.innerHeight;

		if (!sections.length) return;

		let activeSection = null;
		let maxVisibility = 0;

		sections.forEach((section) => {
			const rect = section.getBoundingClientRect();
			const isInActiveZone = rect.top <= viewportHeight * 0.6 && rect.bottom >= viewportHeight * 0.4;

			if (isInActiveZone) {
				const visibleTop = Math.max(0, viewportHeight * 0.4 - rect.top);
				const visibleBottom = Math.min(rect.height, viewportHeight * 0.6 - rect.top);
				const visibleHeight = Math.max(0, visibleBottom - visibleTop);
				const visibilityRatio = visibleHeight / Math.min(rect.height, viewportHeight * 0.2);

				if (visibilityRatio > maxVisibility) {
					maxVisibility = visibilityRatio;
					activeSection = section;
				}
			}
		});

		if (!activeSection) {
			let closestSection = sections[0];
			let minDistance = Infinity;
			const centerY = viewportHeight * 0.5;

			sections.forEach((section) => {
				const rect = section.getBoundingClientRect();
				const sectionCenter = rect.top + rect.height / 2;
				const distance = Math.abs(sectionCenter - centerY);
				if (distance < minDistance) {
					minDistance = distance;
					closestSection = section;
				}
			});

			activeSection = closestSection;
		}

		if (activeSection?.id) {
			const navLink = this.sectionNavMap.get(activeSection.id);
			if (navLink) this.setActiveNavLink(navLink);
		}
	}

	scheduleLottiePlayer() {
		if (this._lottieStarted || !canPlayRailLottie()) return;

		this._lottieStarted = true;

		const start = () => {
			if (this._abort?.signal.aborted) return;
			this.initializeLottiePlayer();
		};

		if (typeof requestIdleCallback === 'function') {
			this._lottieIdleId = requestIdleCallback(start, { timeout: 2500 });
			return;
		}

		this._lottieTimer = window.setTimeout(start, 400);
	}

	loadRailLottie(player, src) {
		if (!src) return;

		this._pendingLottieSrc = src;

		if (!canPlayRailLottie() || !player || typeof player.load !== 'function') {
			return;
		}

		if (player.getAttribute('src') === src && player.loaded) {
			player.play();
			return;
		}

		player.setAttribute('src', src);
		player.load(src).then(() => {
			if (this._abort?.signal.aborted) return;
			player.loaded = true;
			if (this._pendingLottieSrc === src) player.play();
		}).catch((error) => {
			console.error('Error loading rail lottie:', error);
		});
	}

	async initializeLottiePlayer() {
		if (!canPlayRailLottie()) return;

		try {
			await loadDotlottieBundle();
		} catch (error) {
			console.error('Error loading Lottie player:', error);
			return;
		}

		if (this._abort?.signal.aborted) return;

		const player = this.querySelector('[data-rail-lottie]');
		if (!player) return;

		this._lottieObserver?.disconnect();
		this._lottieObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					player.isIntersecting = entry.isIntersecting;

					if (entry.isIntersecting) {
						const src = this._pendingLottieSrc || player.getAttribute('src');
						if (src) this.loadRailLottie(player, src);
					} else if (player.loaded) {
						try {
							player.pause();
						} catch (error) {
							console.warn('Could not pause animation:', error);
						}
					}
				});
			},
			{ root: null, rootMargin: '50px', threshold: 0.1 }
		);

		player.addEventListener(
			'error',
			(error) => console.error('Lottie error:', error),
			this._listenerOptions
		);

		player.addEventListener(
			'complete',
			() => {
				if (player.loaded && player.isIntersecting) player.play();
			},
			this._listenerOptions
		);

		player.loaded = false;
		this._lottieObserver.observe(player);

		if (!this._pendingLottieSrc) {
			const chapter = this.activeDepthChapter || this.querySelector('[data-service-chapter]');
			this._pendingLottieSrc = chapter?.dataset.lottieSrc || '';
		}
	}
}

customElements.define('services-main', ServicesMain);
